import axios, { type InternalAxiosRequestConfig } from "axios";
import {
  logPaymentError,
  getPaymentErrorSource,
  type PaymentFlow,
} from "../utils/paymentErrorLogger";
import { getAccessToken, clearTokens } from "../auth/authStorage";

const baseURL = process.env.REACT_APP_API_URL;

if (!baseURL) {
  throw new Error("REACT_APP_API_URL is not defined in the .env file");
}

const axiosInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

function isPaymentRequest(url?: string): boolean {
  if (!url) return false;
  return (
    url.includes("/Payment/") ||
    url.includes("/SubscriptionPayment/") ||
    url.includes("/Billing/") ||
    url.includes("/payment/") ||
    url.includes("/subscriptionpayment/") ||
    url.includes("/billing/")
  );
}

function getPaymentFlowFromUrl(url?: string): PaymentFlow {
  if (!url) return "payment-api";
  if (url.includes("SubscriptionPayment")) return "membership";
  if (url.includes("/Billing/") || url.includes("/billing/")) return "add-payment-method";
  if (url.includes("Payment")) return "payment-api";
  return "payment-api";
}

/** User-facing validation (e.g. invalid Stripe promo on preview) — show in UI only, do not POST to report-client. */
function shouldOmitClientPaymentErrorReport(error: {
  config?: { url?: string };
  response?: { status?: number };
}): boolean {
  const url = (error.config?.url || "").toLowerCase();
  const status = error.response?.status;
  if (status == null || status >= 500) return false;
  if (url.includes("preview-wallet-pricing") && status >= 400 && status < 500) {
    return true;
  }
  return false;
}

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    if (
      error.config &&
      isPaymentRequest(error.config?.url) &&
      !shouldOmitClientPaymentErrorReport(error)
    ) {
      logPaymentError({
        flow: getPaymentFlowFromUrl(error.config?.url),
        errorSource: "frontend",
        message: "Payment request interceptor error (e.g. payload/header)",
        error,
        requestConfig: error.config
          ? {
              url: error.config.url,
              method: error.config.method,
              baseURL: error.config.baseURL,
              headers: error.config.headers as Record<string, string>,
              params: error.config.params,
            }
          : undefined,
        requestPayload: error.config?.data,
      });
    }
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (
      error.config &&
      isPaymentRequest(error.config?.url) &&
      !shouldOmitClientPaymentErrorReport(error)
    ) {
      const errorSource = getPaymentErrorSource(error);
      const message =
        error.response?.data?.error ||
        error.message ||
        (errorSource === "network" ? "Network error" : "Payment API error");
      try {
        logPaymentError({
          flow: getPaymentFlowFromUrl(error.config?.url),
          errorSource,
          message: `Payment API failed: ${message}`,
          error,
          requestConfig: error.config
            ? {
                url: error.config.url,
                method: error.config.method,
                baseURL: error.config.baseURL,
                headers: error.config.headers as Record<string, string>,
                params: error.config.params,
              }
            : undefined,
          requestPayload: error.config?.data,
          responseStatus: error.response?.status,
          responseData: error.response?.data,
        });
      } catch (e) {
        console.error("[PaymentError] Failed to log payment response error", e);
      }
    }

    // Access token is 30 minutes; do not silent-refresh — force re-login
    if (error.response?.status === 401) {
      clearTokens();
      window.dispatchEvent(new CustomEvent("sessionExpired"));
    }

    return Promise.reject(error);
  }
);

export const getAPIURL = (endpoint: string) => `${baseURL}${endpoint}`;
export const getChatHubUrl = () => `${baseURL.replace(/\/$/, "")}/chatHub`;
export const getNotificationHubUrl = () =>
  `${baseURL.replace(/\/$/, "")}/notificationHub`;
export default axiosInstance;
